import { useId } from 'react';
import type { FormEvent, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ConnectorProfile } from '@/shared/api/generated/catalog';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { SecretField, TextField } from '@/shared/ui/Field';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { CONNECTOR_LABEL } from '@/entities/connection';

import { CHECKS } from '../model/register';
import type {
  Check,
  CheckState,
  EndpointDraft,
  FieldProblem,
  TestOutcome,
} from '../model/register';
import { isRegisterable, offersMechanismChoice } from '../model/registration';
import type { CredentialField, Mechanism } from '../model/registration';
import { ConnectorTile } from './ConnectorTile';
import type { VersionReading } from '../model/version';
import { versionSentence, versionText } from '../model/version';

/* The four steps of registering a source, as the design draws them. Presentational: every decision
 * is made by `RegisterSourceDialog` and the model, and these render what they are handed. */

/* ------------------------------------------------------------------------------------------ 1 */

/** The three things a family can be used for, in the order a source is used. */
const USES = [
  ['metadata', 'harvestable'],
  ['rules', 'executes_rules'],
  ['profiling', 'profiles_columns'],
] as const;

export function SourceTypeStep({
  profiles,
  chosen,
  onChoose,
}: {
  readonly profiles: readonly ConnectorProfile[];
  readonly chosen: ConnectorProfile['connector_type'] | null;
  readonly onChoose: (next: ConnectorProfile['connector_type']) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    /* A radio group of real radios inside labels, so arrow keys move within the group and a card is
       chosen by clicking anywhere on it. An unavailable family is shown and explained — hiding it
       makes the gap invisible — and its radio is disabled so it cannot be chosen. */
    <fieldset className="lq-rg__types">
      <legend className="lq-visually-hidden">{t('register.source.heading')}</legend>
      {profiles.map((profile) => (
        <SourceTypeCard
          key={profile.connector_type}
          profile={profile}
          selected={profile.connector_type === chosen}
          onChoose={onChoose}
        />
      ))}
    </fieldset>
  );
}

/** One family as a card. The radio is named by the family alone and described by what it can be
 * used for, so a screen reader announces "PostgreSQL, radio button" and then the detail, rather than
 * one run-on name. */
function SourceTypeCard({
  profile,
  selected,
  onChoose,
}: {
  readonly profile: ConnectorProfile;
  readonly selected: boolean;
  readonly onChoose: (next: ConnectorProfile['connector_type']) => void;
}): ReactElement {
  const { t } = useTranslation();
  const available = isRegisterable(profile);
  const nameId = useId();
  const detailId = useId();
  return (
    <label
      key={profile.connector_type}
      className="lq-rg__type"
      data-selected={selected ? 'true' : undefined}
      data-unavailable={available ? undefined : 'true'}
    >
      <input
        type="radio"
        name="lq-register-type"
        className="lq-rg__radio"
        value={profile.connector_type}
        checked={selected}
        disabled={!available}
        aria-labelledby={nameId}
        aria-describedby={detailId}
        onChange={() => {
          onChoose(profile.connector_type);
        }}
      />
      <span className="lq-rg__typeHead">
        <ConnectorTile type={profile.connector_type} size="small" />
        <span className="lq-rg__typeName" id={nameId}>
          {CONNECTOR_LABEL[profile.connector_type]}
        </span>
      </span>
      <span className="lq-rg__uses" id={detailId}>
        {USES.map(([use, flag]) => {
          const offered = profile[flag];
          return (
            <span
              key={use}
              className="lq-rg__use"
              data-offered={offered ? 'true' : 'false'}
            >
              <span aria-hidden="true">{offered ? '✓' : '—'}</span>{' '}
              {t(`register.use.${use}`)}
              <span className="lq-visually-hidden">
                {' '}
                {t(offered ? 'register.use.offered' : 'register.use.notOffered')}
              </span>
            </span>
          );
        })}
      </span>
      {!available ? (
        <span className="lq-rg__typeNote">{t('register.source.unavailable')}</span>
      ) : profile.renames_keep_history ? null : (
        <span className="lq-rg__typeNote">{t('register.source.renames')}</span>
      )}
    </label>
  );
}

/* ------------------------------------------------------------------------------------------ 2 */

/** A family reached through its credential alone, and how the dialog says so. */
function hostlessNote(type: ConnectorProfile['connector_type']): string {
  return type === 'snowflake' || type === 'databricks' ? type : 'other';
}

export function CredentialStep({
  profile,
  draft,
  onDraft,
  fields,
  values,
  onValue,
  mechanism,
  onMechanism,
  problems,
  secretKey,
  onSubmit,
  onBack,
}: {
  readonly profile: ConnectorProfile;
  readonly draft: EndpointDraft;
  readonly onDraft: (field: keyof EndpointDraft, value: string) => void;
  readonly fields: readonly CredentialField[];
  /** The credential so far. Only the non-secret fields read it back; a secret field never does —
   * see `SecretField`. */
  readonly values: Readonly<Record<string, string>>;
  readonly onValue: (name: string, value: string) => void;
  readonly mechanism: Mechanism;
  readonly onMechanism: (next: Mechanism) => void;
  readonly problems: Readonly<Record<string, FieldProblem>>;
  /** Changes whenever the secret inputs must empty. They are uncontrolled — see `SecretField`. */
  readonly secretKey: string;
  readonly onSubmit: () => void;
  readonly onBack: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const type = profile.connector_type;
  const problem = (field: string): string | undefined => {
    const found = problems[field];
    return found === undefined ? undefined : t(`register.problem.${found}`);
  };
  const labelOf = (field: CredentialField): string => {
    /* Snowflake's "password" is a Personal Access Token on this platform, so it is named as one. */
    const base =
      type === 'snowflake' && field.name === 'password'
        ? t('connections.field.passwordSnowflake')
        : t(`connections.field.${field.name}`, { defaultValue: field.name });
    return field.required ? base : t('register.cred.optional', { label: base });
  };

  return (
    <form
      className="lq-rg__form"
      /* Inline errors replace the browser's own bubbles, which name one field at a time and cannot be
         styled or translated. */
      noValidate
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="lq-rg__typeBar">
        <ConnectorTile type={type} size="small" />
        <span className="lq-rg__typeBarName">{CONNECTOR_LABEL[type]}</span>
      </div>

      <div className="lq-rg__grid">
        <div className="lq-rg__cell lq-rg__cell--full">
          <TextField
            label={t('register.cred.name')}
            value={draft.name}
            onChange={(value) => {
              onDraft('name', value);
            }}
            placeholder={t('register.cred.namePlaceholder')}
            required
            error={problem('name')}
            autoComplete="off"
          />
        </div>
        {profile.needs_endpoint ? (
          <>
            <div className="lq-rg__cell lq-rg__cell--wide">
              <TextField
                label={t('connections.register.host')}
                value={draft.host}
                onChange={(value) => {
                  onDraft('host', value);
                }}
                placeholder={t('register.cred.hostPlaceholder')}
                required
                mono
                error={problem('host')}
                autoComplete="off"
              />
            </div>
            <div className="lq-rg__cell">
              <TextField
                label={t('connections.register.port')}
                value={draft.port}
                onChange={(value) => {
                  onDraft('port', value);
                }}
                required
                mono
                error={problem('port')}
                autoComplete="off"
              />
            </div>
          </>
        ) : (
          <p className="lq-rg__cell lq-rg__cell--full lq-rg__note">
            {t(`register.cred.hostless.${hostlessNote(type)}`)}
          </p>
        )}
        <div className="lq-rg__cell lq-rg__cell--full">
          <TextField
            label={t('connections.register.database')}
            value={draft.database}
            onChange={(value) => {
              onDraft('database', value);
            }}
            placeholder={
              type === 'snowflake'
                ? t('register.cred.databasePlaceholderSnowflake')
                : t('register.cred.databasePlaceholder')
            }
            required
            mono
            error={problem('database')}
            autoComplete="off"
          />
        </div>
      </div>

      <h3 className="lq-rg__subhead">{t('register.cred.credential')}</h3>

      {offersMechanismChoice(profile) ? (
        <SegmentedFilter
          label={t('connections.register.mechanism')}
          value={mechanism}
          onChange={onMechanism}
          options={[
            { id: 'password', label: t('connections.register.mechanismOption.password') },
            {
              id: 'private_key',
              label: t('connections.register.mechanismOption.private_key'),
            },
          ]}
        />
      ) : null}

      <div className="lq-rg__grid">
        {fields.map((field) => (
          <div
            key={field.name}
            className={
              field.multiline
                ? 'lq-rg__cell lq-rg__cell--full'
                : 'lq-rg__cell lq-rg__cell--half'
            }
          >
            {field.secret ? (
              <SecretField
                label={labelOf(field)}
                /* No value: see `SecretField`. The value lives in the dialog's state for
                   submission and the input holds it only as a DOM property. */
                onChange={(value) => {
                  onValue(field.name, value);
                }}
                required={field.required}
                multiline={field.multiline}
                resetKey={secretKey}
                error={problem(field.name)}
              />
            ) : (
              <TextField
                label={labelOf(field)}
                /* Controlled, unlike the secret fields: an account, a warehouse or an HTTP path is
                   not a secret, and keeping it lets "Edit credential" return to a form that only
                   asks for the secret again. */
                value={values[field.name] ?? ''}
                onChange={(value) => {
                  onValue(field.name, value);
                }}
                placeholder={
                  t(`register.cred.placeholder.${field.name}`, { defaultValue: '' }) ||
                  undefined
                }
                required={field.required}
                mono
                error={problem(field.name)}
                autoComplete="off"
              />
            )}
          </div>
        ))}
      </div>

      <p className="lq-rg__assurance">{t('connections.register.assurance')}</p>

      <div className="lq-rg__actions">
        <Button type="button" onClick={onBack}>
          {t('register.back')}
        </Button>
        <Button tone="primary" type="submit">
          {t('register.cred.submit')}
        </Button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------------------------------ 3 */

function Tick(): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12.5 9.5 18 20 6.5" />
    </svg>
  );
}

function Cross(): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

function Mark({ state }: { readonly state: CheckState }): ReactNode {
  switch (state) {
    case 'passed':
      return <Tick />;
    case 'failed':
      return <Cross />;
    case 'running':
      return <span className="lq-rg__spin" />;
    case 'unconfirmed':
      return <span className="lq-rg__unconfirmed">?</span>;
    case 'pending':
      return null;
  }
}

export function TestStep({
  name,
  states,
  outcome,
  failedStage,
  detail,
  requestError,
  version,
  product,
  onRetry,
  onEdit,
  onContinue,
}: {
  readonly name: string;
  readonly states: Readonly<Record<Check, CheckState>>;
  readonly outcome: TestOutcome;
  readonly failedStage: string | null;
  /** The server's sentence about a refusal. Host-written: the driver's message never reaches it. */
  readonly detail: string | null;
  /** Why the test could not be run at all — a refusal of the request, not of the source. */
  readonly requestError: string | null;
  /** What the test found the source running, and the family's label to say it with. */
  readonly version: VersionReading;
  readonly product: string;
  readonly onRetry: () => void;
  readonly onEdit: () => void;
  readonly onContinue: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const versionLine = versionText(version);
  const stageKey =
    failedStage !== null && (CHECKS as readonly string[]).includes(failedStage)
      ? failedStage
      : 'unknown';

  return (
    <div>
      <p className="lq-rg__lead">{t('register.test.lead', { name })}</p>
      <ol className="lq-rg__checks">
        {CHECKS.map((check) => (
          <li key={check} className="lq-rg__check" data-state={states[check]}>
            <span className="lq-rg__mark" aria-hidden="true">
              <Mark state={states[check]} />
            </span>
            <span className="lq-rg__checkText">{t(`register.check.${check}`)}</span>
            <span className="lq-visually-hidden">
              {t(`register.checkState.${states[check]}`)}
            </span>
          </li>
        ))}
      </ol>

      {/* Announced when it changes, so a screen-reader user who started the test hears its result. */}
      <div aria-live="polite">
        {outcome === 'reachable' ? (
          <div
            className="lq-rg__result"
            data-tone={version.kind === 'limited' ? 'warning' : 'ok'}
          >
            <p className="lq-rg__resultLine">{t('register.test.reachable')}</p>
            {versionLine === null ? null : (
              <p className="lq-rg__resultDetail">
                {t('register.test.version', { product, version: versionLine })}
                {version.kind === 'limited' ? (
                  <> {versionSentence(t, version, product)}</>
                ) : null}
              </p>
            )}
          </div>
        ) : outcome === 'unasked' ? (
          <Alert tone="warning" title={t('register.test.unaskedTitle')}>
            {requestError ?? t('register.test.unasked')}
          </Alert>
        ) : outcome === 'refused' ? (
          <div className="lq-rg__result" data-tone="error">
            <p className="lq-rg__resultLine">{t(`register.test.refused.${stageKey}`)}</p>
            {/* The server's own sentence: which address, and the failure's kind. */}
            {detail === null ? null : <p className="lq-rg__resultDetail">{detail}</p>}
          </div>
        ) : null}
      </div>

      <p className="lq-rg__note">{t('register.test.scope')}</p>

      <div className="lq-rg__actions">
        <Button type="button" onClick={onEdit} disabled={outcome === 'running'}>
          {t('register.test.edit')}
        </Button>
        <Button type="button" onClick={onRetry} busy={outcome === 'running'}>
          {t('register.test.retry')}
        </Button>
        <Button
          tone="primary"
          type="button"
          onClick={onContinue}
          /* Only a passing test opens the next step, because finishing there is what stores the
             source — and a source is stored only once it has been reached. */
          disabled={outcome !== 'reachable'}
          title={
            outcome === 'reachable' ? undefined : t('register.test.continueNeedsPass')
          }
        >
          {t('register.test.continue')}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------ 4 */

export interface PreviewReading {
  readonly kind: 'loading' | 'failed' | 'ready' | 'notRead';
  readonly message?: string;
  readonly schemas?: readonly { readonly name: string; readonly objects: number }[];
  readonly totalSchemas?: number;
  readonly totalObjects?: number;
}

export function DatasetsStep({
  preview,
  reachable,
  profilingOffered,
  auto,
  onAuto,
  finishing,
  failure,
  locked,
  onHarvestNow,
  onHarvestLater,
  onBack,
}: {
  readonly preview: PreviewReading;
  /** Whether the test passed: a harvest needs a source that answered. */
  readonly reachable: boolean;
  /** Whether this family can be profiled on this deployment at all. */
  readonly profilingOffered: boolean;
  readonly auto: boolean;
  readonly onAuto: (next: boolean) => void;
  readonly finishing: 'now' | 'later' | null;
  readonly failure: { readonly title: string; readonly detail: string } | null;
  /** The source is registered: there is no going back, only finishing. */
  readonly locked: boolean;
  readonly onHarvestNow: () => void;
  readonly onHarvestLater: () => void;
  readonly onBack: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const autoUsable = profilingOffered && reachable;
  const labelId = useId();
  const noteId = useId();

  return (
    <div className="lq-rg__datasets">
      <h3 className="lq-rg__subhead">{t('register.datasets.heading')}</h3>
      {preview.kind === 'loading' ? (
        <SkeletonRows rows={3} columns={2} />
      ) : preview.kind === 'notRead' ? (
        <p className="lq-rg__note">{t('register.datasets.notRead')}</p>
      ) : preview.kind === 'failed' ? (
        <p className="lq-rg__note">
          {t('register.datasets.unreadable')} {preview.message}
        </p>
      ) : (preview.schemas ?? []).length === 0 ? (
        <p className="lq-rg__note">{t('register.datasets.none')}</p>
      ) : (
        <>
          <ul className="lq-rg__schemas">
            {(preview.schemas ?? []).map((schema) => (
              <li key={schema.name} className="lq-rg__schema">
                <span className="lq-rg__schemaName">{schema.name}</span>
                <span className="lq-rg__schemaCount">
                  {t('register.datasets.objects', { count: schema.objects })}
                </span>
              </li>
            ))}
          </ul>
          <p className="lq-rg__total">
            {t('register.datasets.total', {
              objects: preview.totalObjects ?? 0,
              schemas: preview.totalSchemas ?? 0,
            })}
          </p>
          <p className="lq-rg__note">{t('register.datasets.scope')}</p>
        </>
      )}

      <div className="lq-rg__auto" data-disabled={autoUsable ? undefined : 'true'}>
        <button
          type="button"
          role="switch"
          className="lq-switch"
          aria-checked={autoUsable && auto}
          aria-labelledby={labelId}
          aria-describedby={noteId}
          disabled={!autoUsable}
          onClick={() => {
            onAuto(!auto);
          }}
        >
          <span className="lq-switch__thumb" />
        </button>
        <div className="lq-rg__autoText">
          <span className="lq-rg__autoLabel" id={labelId}>
            {t('register.auto.label')}
          </span>
          <span className="lq-rg__autoNote" id={noteId}>
            {!profilingOffered
              ? t('register.auto.unavailable')
              : !reachable
                ? t('register.auto.needsTest')
                : t('autoProfile.costNote')}
          </span>
        </div>
      </div>

      {failure === null ? null : (
        <Alert tone="error" title={failure.title}>
          {failure.detail}
        </Alert>
      )}

      <div className="lq-rg__actions">
        {locked ? null : (
          <Button type="button" onClick={onBack} disabled={finishing !== null}>
            {t('register.back')}
          </Button>
        )}
        <Button
          type="button"
          onClick={onHarvestLater}
          busy={finishing === 'later'}
          disabled={finishing === 'now'}
        >
          {t('register.harvestLater')}
        </Button>
        <Button
          tone="primary"
          type="button"
          onClick={onHarvestNow}
          busy={finishing === 'now'}
          disabled={!reachable || finishing === 'later'}
          title={reachable ? undefined : t('register.harvestNeedsTest')}
        >
          {t('register.harvestNow')}
        </Button>
      </div>
    </div>
  );
}
